/**
 * React hooks for form validation across database object creation/editing forms
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { FormValidator, createValidationAggregator } from '@/utils/formValidation'

export interface ValidationState {
  errors: Record<string, string>
  warnings: Record<string, string>
  isValid: boolean
  touchedFields: Set<string>
}

export interface ValidationFieldConfig {
  required?: boolean
  type?: 'identifier' | 'sql' | 'email' | 'password' | 'numeric' | 'array' | 'custom'
  maxLength?: number
  minLength?: number
  min?: number
  max?: number
  customValidator?: (value: unknown) => string
  uniqueValidator?: (value: string) => Promise<boolean>
}

/**
 * Main form validation hook
 */
export const useFormValidation = (
  connection: DatabaseConnection,
  existingNames: string[] = []
) => {
  const [validationState, setValidationState] = useState<ValidationState>({
    errors: {},
    warnings: {},
    isValid: true,
    touchedFields: new Set()
  })

  const validator = useMemo(() => new FormValidator(connection, existingNames), [connection, existingNames])

  /**
   * Validate a single field
   */
  const validateField = useCallback(
    async (fieldName: string, value: unknown, config: ValidationFieldConfig = {}) => {
      let error = ''

      // Required validation
      if (config.required && (!value || (typeof value === 'string' && !value.trim()))) {
        error = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`
      }

      // Type-specific validation
      if (!error && value) {
        switch (config.type) {
          case 'identifier':
            error = validator.validateIdentifier(typeof value === 'string' ? value : JSON.stringify(value), fieldName, config.maxLength)
            break
          case 'sql':
            error = validator.validateSQLContent(typeof value === 'string' ? value : JSON.stringify(value), fieldName)
            break
          case 'email':
            error = validator.validateEmail(typeof value === 'string' ? value : JSON.stringify(value), fieldName)
            break
          case 'password':
            error = validator.validatePassword(typeof value === 'string' ? value : JSON.stringify(value), fieldName)
            break
          case 'numeric':
            error = validator.validateNumeric((typeof value === 'number' || typeof value === 'string') ? value : Number(value), fieldName, config.min, config.max)
            break
          case 'array':
            error = validator.validateArray(Array.isArray(value) ? value : [], fieldName, config.minLength, config.maxLength)
            break
          case 'custom':
            if (config.customValidator) {
              error = config.customValidator(value)
            }
            break
          case undefined:
            // No validation for undefined type
            break
        }
      }

      // Uniqueness validation
      if (!error && config.uniqueValidator && value) {
        try {
          const isUnique = await config.uniqueValidator(typeof value === 'string' ? value : JSON.stringify(value))
          if (!isUnique) {
            error = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} already exists`
          }
        } catch (e) {
          // Handle uniqueness check errors gracefully
          console.warn('Uniqueness validation failed:', e)
        }
      }

      // Update validation state
      setValidationState(prev => {
        const newTouchedFields = new Set(prev.touchedFields)
        newTouchedFields.add(fieldName)

        let newErrors: Record<string, string>
        if (error) {
          newErrors = { ...prev.errors, [fieldName]: error }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [fieldName]: _, ...remainingErrors } = prev.errors
          newErrors = remainingErrors
        }

        return {
          ...prev,
          errors: newErrors,
          touchedFields: newTouchedFields,
          isValid: Object.keys(newErrors).length === 0
        }
      })

      return error
    },
    [validator]
  )

  /**
   * Validate entire form
   */
  const validateForm = useCallback(
    async (formData: Record<string, unknown>, fieldConfigs: Record<string, ValidationFieldConfig>) => {
      const aggregator = createValidationAggregator()

      // Validate all fields
      for (const [fieldName, value] of Object.entries(formData)) {
        const config = fieldConfigs[fieldName] ?? {}
        const error = await validateField(fieldName, value, config)
        aggregator.addError(fieldName, error)
      }

      const result = aggregator.getResult()
      
      setValidationState(prev => ({
        ...prev,
        errors: result.errors,
        warnings: result.warnings,
        isValid: result.isValid
      }))

      return result
    },
    [validateField]
  )

  /**
   * Clear validation errors
   */
  const clearValidation = useCallback(() => {
    setValidationState({
      errors: {},
      warnings: {},
      isValid: true,
      touchedFields: new Set()
    })
  }, [])

  /**
   * Clear error for specific field
   */
  const clearFieldError = useCallback((fieldName: string) => {
    setValidationState(prev => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fieldName]: _, ...newErrors } = prev.errors

      return {
        ...prev,
        errors: newErrors,
        isValid: Object.keys(newErrors).length === 0
      }
    })
  }, [])

  /**
   * Set custom error
   */
  const setFieldError = useCallback((fieldName: string, error: string) => {
    setValidationState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [fieldName]: error
      },
      isValid: false
    }))
  }, [])

  /**
   * Mark field as touched
   */
  const touchField = useCallback((fieldName: string) => {
    setValidationState(prev => ({
      ...prev,
      touchedFields: new Set(prev.touchedFields).add(fieldName)
    }))
  }, [])

  /**
   * Get field validation status
   */
  const getFieldStatus = useCallback(
    (fieldName: string) => {
      const hasError = !!validationState.errors[fieldName]
      const isTouched = validationState.touchedFields.has(fieldName)
      
      return {
        hasError,
        isTouched,
        error: validationState.errors[fieldName] || '',
        shouldShowError: hasError && isTouched
      }
    },
    [validationState]
  )

  return {
    validationState,
    validateField,
    validateForm,
    clearValidation,
    clearFieldError,
    setFieldError,
    touchField,
    getFieldStatus,
    isValid: validationState.isValid,
    hasErrors: Object.keys(validationState.errors).length > 0,
    errors: validationState.errors,
    warnings: validationState.warnings
  }
}

/**
 * Hook for real-time field validation
 */
export const useFieldValidation = (
  fieldName: string,
  value: unknown,
  config: ValidationFieldConfig,
  connection: DatabaseConnection,
  existingNames: string[] = []
) => {
  const [error, setError] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const validator = useMemo(() => new FormValidator(connection, existingNames), [connection, existingNames])

  useEffect(() => {
    const validateAsync = async () => {
      setIsValidating(true)
      
      let validationError = ''

      // Required validation
      if (config.required && (!value || (typeof value === 'string' && !value.trim()))) {
        validationError = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`
      }

      // Type-specific validation
      if (!validationError && value) {
        switch (config.type) {
          case 'identifier': {
            const strValue = typeof value === 'string' ? value : JSON.stringify(value)
            validationError = validator.validateIdentifier(strValue, fieldName, config.maxLength)
            break
          }
          case 'sql': {
            const strValue = typeof value === 'string' ? value : JSON.stringify(value)
            validationError = validator.validateSQLContent(strValue, fieldName)
            break
          }
          case 'email': {
            const strValue = typeof value === 'string' ? value : JSON.stringify(value)
            validationError = validator.validateEmail(strValue, fieldName)
            break
          }
          case 'password': {
            const strValue = typeof value === 'string' ? value : JSON.stringify(value)
            validationError = validator.validatePassword(strValue, fieldName)
            break
          }
          case 'numeric': {
            const numOrString = (typeof value === 'number' || typeof value === 'string') ? value : Number(value)
            validationError = validator.validateNumeric(numOrString, fieldName, config.min, config.max)
            break
          }
          case 'array': {
            const arrValue = Array.isArray(value) ? value : []
            validationError = validator.validateArray(arrValue as unknown[], fieldName, config.minLength, config.maxLength)
            break
          }
          case 'custom': {
            if (config.customValidator) {
              validationError = config.customValidator(value)
            }
            break
          }
          case undefined:
            // No validation for undefined type
            break
        }
      }

      // Uniqueness validation
      if (!validationError && config.uniqueValidator && value) {
        try {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          const isUnique = await config.uniqueValidator(strValue)
          if (!isUnique) {
            validationError = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} already exists`
          }
        } catch (e) {
          console.warn('Uniqueness validation failed:', e)
        }
      }

      setError(validationError)
      setIsValidating(false)
    }

    const debounceTimer = setTimeout(() => { void validateAsync() }, 300)
    return () => { clearTimeout(debounceTimer) }
  }, [value, fieldName, config, validator])

  return { error, isValidating }
}

/**
 * Form validation configuration presets for different object types
 */
export const getValidationConfig = (objectType: string, connection: DatabaseConnection) => {
  const baseConfig = {
    identifier: {
      required: true,
      type: 'identifier' as const,
      maxLength: connection.type === 'mysql' ? 64 : 63
    },
    sql: {
      required: true,
      type: 'sql' as const
    },
    email: {
      required: false,
      type: 'email' as const
    },
    password: {
      required: true,
      type: 'password' as const
    }
  }

  switch (objectType) {
    case 'database':
      return {
        name: baseConfig.identifier,
        characterSet: { required: false, type: 'identifier' as const },
        collation: { required: false, type: 'identifier' as const },
        comment: { required: false, maxLength: 1000 }
      }

    case 'schema':
      return {
        name: baseConfig.identifier,
        owner: { required: false, type: 'identifier' as const },
        comment: { required: false, maxLength: 1000 }
      }

    case 'table':
      return {
        name: baseConfig.identifier,
        comment: { required: false, maxLength: 1000 },
        engine: { required: false, type: 'identifier' as const },
        characterSet: { required: false, type: 'identifier' as const },
        collation: { required: false, type: 'identifier' as const }
      }

    case 'column':
      return {
        name: baseConfig.identifier,
        type: { required: true, type: 'identifier' as const },
        length: { required: false, type: 'numeric' as const, min: 1 },
        precision: { required: false, type: 'numeric' as const, min: 1 },
        scale: { required: false, type: 'numeric' as const, min: 0 },
        defaultValue: { required: false },
        comment: { required: false, maxLength: 1000 }
      }

    case 'index':
      return {
        name: baseConfig.identifier,
        table: { required: true, type: 'identifier' as const },
        columns: { required: true, type: 'array' as const, minLength: 1 },
        whereClause: { required: false, type: 'sql' as const }
      }

    case 'view':
      return {
        name: baseConfig.identifier,
        definition: baseConfig.sql,
        comment: { required: false, maxLength: 1000 }
      }

    case 'procedure':
    case 'function':
      return {
        name: baseConfig.identifier,
        parameters: { required: false, type: 'sql' as const },
        body: baseConfig.sql,
        returnType: { required: objectType === 'function', type: 'identifier' as const },
        comment: { required: false, maxLength: 1000 }
      }

    case 'trigger':
      return {
        name: baseConfig.identifier,
        table: { required: true, type: 'identifier' as const },
        timing: { required: true, type: 'identifier' as const },
        event: { required: true, type: 'identifier' as const },
        body: baseConfig.sql,
        comment: { required: false, maxLength: 1000 }
      }

    case 'user':
      return {
        name: baseConfig.identifier,
        password: baseConfig.password,
        email: baseConfig.email,
        host: { required: false, type: 'identifier' as const },
        comment: { required: false, maxLength: 1000 }
      }

    case 'sequence':
      return {
        name: baseConfig.identifier,
        startValue: { required: false, type: 'numeric' as const },
        increment: { required: false, type: 'numeric' as const },
        minValue: { required: false, type: 'numeric' as const },
        maxValue: { required: false, type: 'numeric' as const },
        cache: { required: false, type: 'numeric' as const, min: 1 },
        comment: { required: false, maxLength: 1000 }
      }

    case 'extension':
      return {
        name: baseConfig.identifier,
        schema: { required: false, type: 'identifier' as const },
        version: { required: false, type: 'identifier' as const },
        cascade: { required: false }
      }

    case 'row':
      return {} // Dynamic based on table structure

    default:
      return {
        name: baseConfig.identifier
      }
  }
}
